import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import sys32logo from "../../assets/images/sys3222.png";
import { useLanguage } from "../../i18n/LanguageContext";
import "./Navbar.css";

const services = [
  { label: "Azure Governance",       path: "/services/azure-cloud",          icon: "🛡️" },
  { label: "AI Governance",          path: "/services/ai-governance",        icon: "🤖" },
  { label: "Software Development",   path: "/services/software-development", icon: "💻" },
  { label: "Automation Services",    path: "/services/automation",           icon: "⚡" },
  { label: "AI Automation",          path: "/services/ai-automation",        icon: "🧠" },
  { label: "Microsoft Azure",        path: "/services/azure",                icon: "☁️" },
];

const useCases = [
  {
    title: "Moving 400+ Servers to Azure for a Danish Utility Leader",
    desc: "One of the first forsyning companies to go cloud-only — 400+ servers out of the basement.",
    path: "/use-cases/azure-migration-danish-sme",
  },
  {
    title: "AI Guardrails for Financial Services",
    desc: "Microsoft Purview, Content Safety, and Responsible AI logging.",
    path: "/use-cases/ai-guardrails-financial-services",
  },
  {
    title: "CI/CD Transformation with Azure DevOps",
    desc: "Replaced publish-profile deployments with automated pipelines.",
    path: "/use-cases/cicd-transformation-azure-devops",
  },
  {
      title: "Access Package Governance for Production Access",
      desc: "Audited, policy-based access for groups, shared mailboxes, and production resources.",
    path: "/use-cases/power-platform-operations-automation",
  },
  {
    title: "Entra ID Rollout Across 200-Person Org",
    desc: "6 identity systems consolidated to 1 with full MFA enforcement.",
    path: "/use-cases/entra-id-rollout-200-person-org",
  },
  {
    title: "Azure & Oracle Exascale Latency Reduction",
    desc: "TCP latency to Oracle listener cut to ~1 ms with PPGs and AZ alignment.",
    path: "/use-cases/oracle-azure-latency-reduction",
  },
  {
    title: "Event-Driven Platform for Utility Operations",
    desc: "Credential-free, event-driven Azure platform with built-in observability.",
    path: "/use-cases/event-driven-utility-platform",
  },
  {
    title: "Centralising Scattered APIs Under One Secure Gateway",
    desc: "Azure APIM + Application Gateway WAF — one policy engine, zero direct backend exposure.",
    path: "/use-cases/api-centralisation-azure-apim",
  },
];

const featuredUseCases = useCases.slice(0, 3);

const servicesDa = [
  { label: "Azure Governance", path: "/services/azure-cloud", icon: "🛡️" },
  { label: "AI Governance", path: "/services/ai-governance", icon: "🤖" },
  { label: "Softwareudvikling", path: "/services/software-development", icon: "💻" },
  { label: "Automatisering", path: "/services/automation", icon: "⚡" },
  { label: "AI Automation", path: "/services/ai-automation", icon: "🧠" },
  { label: "Microsoft Azure", path: "/services/azure", icon: "☁️" },
];

const useCasesDa = [
  {
    title: "400+ servere flyttet til Azure",
    desc: "Cloud-migrering, landing zone og governance for en dansk forsyningsorganisation.",
    path: "/use-cases/azure-migration-danish-sme",
  },
  {
    title: "AI-guardrails for finansiel virksomhed",
    desc: "Microsoft Purview, Content Safety og ansvarlig AI-logning.",
    path: "/use-cases/ai-guardrails-financial-services",
  },
  {
    title: "CI/CD-transformation med Azure DevOps",
    desc: "Fra publish-profiler til automatiserede pipelines.",
    path: "/use-cases/cicd-transformation-azure-devops",
  },
];

const Navigationbar = () => {
  const { language, setLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [useCasesOpen, setUseCasesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileUseCasesOpen, setMobileUseCasesOpen] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);
  const useCasesRef = useRef(null);
  const isDa = language === "da";
  const navServices = isDa ? servicesDa : services;
  const navUseCases = isDa ? useCasesDa : featuredUseCases;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
      if (useCasesRef.current && !useCasesRef.current.contains(e.target)) setUseCasesOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); setServicesOpen(false); setUseCasesOpen(false); setMobileServicesOpen(false); setMobileUseCasesOpen(false); }, [location]);

  return (
    <nav className={`nb-nav${scrolled ? " nb-nav--scrolled" : ""}`}>
      <div className="nb-inner">
        {/* Brand */}
        <Link to="/" className="nb-brand">
          <img src={sys32logo} alt="System32" className="nb-logo" />
          <span className="nb-brand-name">System32</span>
        </Link>

        {/* Desktop links */}
        <div className="nb-links">
          <Link to="/" className="nb-link">{isDa ? "Forside" : "Home"}</Link>

          {/* Services dropdown */}
          <div className="nb-dropdown-wrap" ref={servicesRef}>
            <button
              className="nb-link nb-link--btn"
              onClick={() => { setServicesOpen(o => !o); setUseCasesOpen(false); }}
              aria-expanded={servicesOpen}
            >
              {isDa ? "Ydelser" : "Services"}
              <svg className={`nb-chevron${servicesOpen ? " nb-chevron--open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6,9 12,15 18,9"/></svg>
            </button>
            {servicesOpen && (
              <div className="nb-dropdown">
                <div className="nb-dropdown-label">{isDa ? "Det vi leverer" : "What we deliver"}</div>
                {navServices.map(s => (
                  <Link key={s.path} to={s.path} className="nb-dropdown-item">
                    <span className="nb-di-icon">{s.icon}</span>
                    <span>{s.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Use Cases dropdown */}
          <div className="nb-dropdown-wrap" ref={useCasesRef}>
            <button
              className="nb-link nb-link--btn"
              onClick={() => { setUseCasesOpen(o => !o); setServicesOpen(false); }}
              aria-expanded={useCasesOpen}
            >
              {isDa ? "Cases" : "Use Cases"}
              <svg className={`nb-chevron${useCasesOpen ? " nb-chevron--open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6,9 12,15 18,9"/></svg>
            </button>
            {useCasesOpen && (
              <div className="nb-dropdown nb-dropdown--wide">
                <div className="nb-dropdown-label">{isDa ? "Kundecases" : "Client stories"}</div>
                {navUseCases.map(uc => (
                  <Link key={uc.title} to={uc.path} className="nb-dropdown-item nb-dropdown-item--story">
                    <span className="nb-story-title">{uc.title}</span>
                    <span className="nb-story-desc">{uc.desc}</span>
                  </Link>
                ))}
                <div style={{borderTop:"1px solid rgba(15,23,42,0.08)",margin:"6px 0 2px"}} />
                <Link to="/use-cases" className="nb-dropdown-item" style={{fontWeight:700,color:"#2563eb"}}>
                  {isDa ? "Se alle cases →" : "View all use cases →"}
                </Link>
              </div>
            )}
          </div>

          <Link to="/about" className="nb-link">{isDa ? "Om os" : "About"}</Link>
          <a href="/#contact-section" className="nb-link">{isDa ? "Kontakt" : "Contact"}</a>
          <button className="nb-language" onClick={() => setLanguage(isDa ? "en" : "da")} aria-label="Change language">
            {isDa ? "EN" : "DA"}
          </button>
          <a href="/#contact-section" className="nb-cta">{isDa ? "Book en strategisamtale" : "Book a strategy call"}</a>
        </div>

        {/* Mobile hamburger */}
        <button
          className={`nb-burger${mobileOpen ? " nb-burger--open" : ""}`}
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="nb-mobile">
          <Link to="/" className="nb-mobile-link">{isDa ? "Forside" : "Home"}</Link>

          <div className="nb-mobile-section">
            <button
              className="nb-mobile-section-toggle"
              onClick={() => setMobileServicesOpen(o => !o)}
              aria-expanded={mobileServicesOpen}
            >
              {isDa ? "Ydelser" : "Services"}
              <svg className={`nb-chevron${mobileServicesOpen ? " nb-chevron--open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6,9 12,15 18,9"/></svg>
            </button>
            {mobileServicesOpen && navServices.map(s => (
              <Link key={s.path} to={s.path} className="nb-mobile-link nb-mobile-link--sub">
                {s.icon} {s.label}
              </Link>
            ))}
          </div>

          <div className="nb-mobile-section">
            <button
              className="nb-mobile-section-toggle"
              onClick={() => setMobileUseCasesOpen(o => !o)}
              aria-expanded={mobileUseCasesOpen}
            >
              {isDa ? "Cases" : "Use Cases"}
              <svg className={`nb-chevron${mobileUseCasesOpen ? " nb-chevron--open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6,9 12,15 18,9"/></svg>
            </button>
            {mobileUseCasesOpen && navUseCases.map(uc => (
              <Link key={uc.title} to={uc.path} className="nb-mobile-link nb-mobile-link--sub">
                {uc.title}
              </Link>
            ))}
            {mobileUseCasesOpen && (
              <Link to="/use-cases" className="nb-mobile-link nb-mobile-link--sub nb-mobile-link--more">
                {isDa ? "Se alle cases →" : "View all use cases →"}
              </Link>
            )}
          </div>

          <Link to="/about" className="nb-mobile-link">{isDa ? "Om os" : "About"}</Link>
          <a href="/#contact-section" className="nb-mobile-link">{isDa ? "Kontakt" : "Contact"}</a>
          <button className="nb-mobile-link nb-mobile-language" onClick={() => setLanguage(isDa ? "en" : "da")}>{isDa ? "Switch to English" : "Skift til dansk"}</button>
          <a href="/#contact-section" className="nb-mobile-cta">{isDa ? "Book en strategisamtale →" : "Book a strategy call →"}</a>
        </div>
      )}
    </nav>
  );
};

export default Navigationbar;
